import { roundToDecimals } from '../math.helpers';

/**
 * Lightweight pipeline profiler for measuring enrichProject performance.
 * Gated by {@link PIPELINE_PROFILE} — zero overhead when disabled.
 *
 * Usage:
 *   profiler.reset();
 *   profiler.time('step');
 *   // ... work ...
 *   profiler.timeEnd('step');
 *   profiler.count('someFunction');
 *   profiler.summary();
 */

const PIPELINE_PROFILE = false;

type ProfileData = {
  timings: Map<string, { total: number; starts: number[] }>;
  counts: Map<string, number>;
};

const data: ProfileData = {
  timings: new Map(),
  counts: new Map(),
};

const reset = (): void => {
  if (!PIPELINE_PROFILE) return;
  data.timings.clear();
  data.counts.clear();
};

const time = (label: string): void => {
  if (!PIPELINE_PROFILE) return;
  const entry = data.timings.get(label) ?? { total: 0, starts: [] };
  entry.starts.push(performance.now());
  data.timings.set(label, entry);
};

const timeEnd = (label: string): void => {
  if (!PIPELINE_PROFILE) return;
  const entry = data.timings.get(label);
  if (!entry?.starts.length) return;
  const startedAt = entry.starts.pop();
  if (startedAt === undefined) return;
  entry.total += performance.now() - startedAt;
};

const count = (label: string): void => {
  if (!PIPELINE_PROFILE) return;
  data.counts.set(label, (data.counts.get(label) ?? 0) + 1);
};

const countAdd = (label: string, n: number): void => {
  if (!PIPELINE_PROFILE) return;
  data.counts.set(label, (data.counts.get(label) ?? 0) + n);
};

const summary = (): void => {
  if (!PIPELINE_PROFILE) return;

  const timingRows = Array.from(data.timings.entries())
    .map(([label, { total }]) => ({
      label,
      ms: roundToDecimals(total),
    }))
    .sort((a, b) => b.ms - a.ms);

  const countRows = Array.from(data.counts.entries())
    .map(([label, n]) => ({ label, count: n }))
    .sort((a, b) => b.count - a.count);

  console.group('[Pipeline Profiler]');
  if (timingRows.length) {
    console.table(timingRows);
  }
  if (countRows.length) {
    console.table(countRows);
  }
  console.groupEnd();
};

export const profiler = { reset, time, timeEnd, count, countAdd, summary };
